import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  TextField,
  useScrollTrigger,
  InputAdornment,
  Menu,
  MenuItem
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { Link } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';

export default function MainNavigation({
  onSettingsClick,
  userLat,
  userLon,
  address,
  setAddress,
  onFindMe,
  onHomeAddressSearch,
  onMapAddressCenter,
}) {
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 2 });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleAddressSearch = async () => {
    const currentPath = window.location.pathname;

    // 1) "lat,lon"
    const coordMatch =
      address && address.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
    if (coordMatch) {
      const latNum = parseFloat(coordMatch[1]);
      const lonNum = parseFloat(coordMatch[2]);
      setAddress?.(`${latNum.toFixed(5)}, ${lonNum.toFixed(5)}`);
      if (currentPath === "/") onHomeAddressSearch?.(latNum, lonNum);
      else if (currentPath === "/map") onMapAddressCenter?.(latNum, lonNum);
      return;
    }

    // 2) Empty input but coords exist
    if ((!address || address.trim() === "") && userLat != null && userLon != null) {
      setAddress?.(`${Number(userLat).toFixed(5)}, ${Number(userLon).toFixed(5)}`);
      if (currentPath === "/") onHomeAddressSearch?.(userLat, userLon);
      else if (currentPath === "/map") onMapAddressCenter?.(userLat, userLon);
      return;
    }

    // 3) Geocode address
    if (!address || !address.trim()) return;
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
      address
    )}&limit=1`;
    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        const latNum = parseFloat(lat);
        const lonNum = parseFloat(lon);
        setAddress?.(`${latNum.toFixed(5)}, ${lonNum.toFixed(5)}`);
        if (currentPath === "/") onHomeAddressSearch?.(latNum, lonNum);
        else if (currentPath === "/map") onMapAddressCenter?.(latNum, lonNum);
      } else {
        alert("Address not found.");
      }
    } catch (err) {
      console.error("Geocoding error:", err);
    }
  };

  // ✅ Check login state via secure cookie
  useEffect(() => {
    fetch("/authapi/auth_check.php", {
      credentials: "include"
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.loggedIn) {
          setIsLoggedIn(true);
        } else {
          setIsLoggedIn(false);
        }
      })
      .catch(() => setIsLoggedIn(false));
  }, []);

  return (
    <AppBar
      position="sticky"
      elevation={trigger ? 4 : 0}
      sx={{ top: 0, zIndex: 1200, transition: 'box-shadow .2s ease' }}
    >
      <Toolbar sx={{ gap: 1, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          FlightIQ <span style={{ color: 'red', fontStyle: 'italic' }}>(Beta)</span>
        </Typography>

        {/* Search + GO */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            flexGrow: 1,
            maxWidth: '600px',
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            placeholder="Enter address or lat,lon"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            sx={{ width: '50%', minWidth: 150 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="use current location"
                    onClick={() =>
                      onFindMe({ fillOnly: true }, (lat, lon) =>
                        setAddress(`${lat.toFixed(5)}, ${lon.toFixed(5)}`)
                      )
                    }
                    edge="end"
                  >
                    <MyLocationIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" onClick={handleAddressSearch} sx={{ flexShrink: 0 }}>
            GO
          </Button>
        </Box>

        {/* Nav links */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 2 }}>
          <Button color="inherit" component={Link} to="/">
            Home
          </Button>
          <Button color="inherit" component={Link} to="/map">
            Map
          </Button>
          <Button color="inherit" component={Link} to="/about">
            About
          </Button>
        </Box>

        <IconButton color="inherit" onClick={onSettingsClick} sx={{ ml: 2 }}>
          <SettingsIcon />
        </IconButton>

        {/* Account menu */}
         <IconButton color="inherit" onClick={handleMenuOpen}>
           <AccountCircle style={{ color: user ? "limegreen" : "white" }} />
         </IconButton>
<Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
  {!user ? (
    <>
      <MenuItem component={Link} to="/login" onClick={handleMenuClose}>
        Login
      </MenuItem>
      <MenuItem component={Link} to="/register" onClick={handleMenuClose}>
        Register
      </MenuItem>
    </>
  ) : (
    <>
      <MenuItem disabled>Welcome, {user.username}</MenuItem>
      <MenuItem component={Link} to="/profile" onClick={handleMenuClose}>
        Profile
      </MenuItem>
      {/* ✅ Only show for admins */}
{user.role === "admin" && (
  <MenuItem component={Link} to="/sysop" onClick={handleMenuClose}>
    Admin
  </MenuItem>
)}
      <MenuItem
        onClick={() => {
          logout();
          handleMenuClose();
        }}
      >
        Logout
      </MenuItem>
    </>
  )}
</Menu>
      </Toolbar>
    </AppBar>
  );
}
